import React, { useState } from 'react';
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormClear } from '@/shared/hooks/useFormClear.ts';
import { cn } from '@/shared/utils/cn.ts';
import { BILLING_TYPE } from '@schuettflix/interfaces';
import { PaymentTermsSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import { SelectCombobox } from '@/shared/ui/Combobox/SelectCombobox.tsx';

type PaymentTermsIdFieldValues = {
    [key: string]: PaymentTermsSettings['paymentTermId'];
};

type BillingMethodFieldValues = {
    [key: string]: PaymentTermsSettings['billingMethod'];
};

type PaymentTermOption = { value: number; label: string };
type BillingMethodOption = { value: BILLING_TYPE; label: string };

type PaymentTermsInputProps = {
    paymentTermsIdProps: Pick<
        UseControllerProps<PaymentTermsIdFieldValues, string>,
        'name' | 'defaultValue' | 'disabled'
    > & {
        options: PaymentTermOption[];
        onChangePaymentTermsId?: (paymentTermsId: number | null) => void;
    };
    billingMethodProps: Pick<
        UseControllerProps<BillingMethodFieldValues, string>,
        'name' | 'defaultValue' | 'disabled'
    > & {
        options: BillingMethodOption[];
        onBillingMethodChange?: (billingMethod?: BILLING_TYPE) => void;
    };
};

export const PaymentTermsInput = React.forwardRef<HTMLInputElement, PaymentTermsInputProps>(
    ({ paymentTermsIdProps, billingMethodProps }, _ref) => {
        const form = useFormContext();

        const { t } = useTranslation();
        const { control, clearField } = useFormClear(form);

        const [isPaymentTermIdDisabled, setIsPaymentTermIdDisabled] = useState<boolean>(
            billingMethodProps.defaultValue === 'PARTNER_INVOICE'
        );

        return (
            <div className="grid grid-cols-2 items-start gap-2">
                <Controller
                    control={control}
                    name={paymentTermsIdProps.name}
                    defaultValue={paymentTermsIdProps.defaultValue ?? null}
                    render={({ field: { ref, onChange, value }, fieldState: { error, invalid } }) => (
                        <SelectCombobox
                            fieldRef={ref}
                            value={value != null ? String(value) : undefined}
                            onValueChange={idStr => {
                                const paymentTermId = Number(idStr);

                                paymentTermsIdProps.onChangePaymentTermsId?.(paymentTermId);
                                onChange(paymentTermId);
                            }}
                            options={paymentTermsIdProps.options.map(o => ({
                                value: String(o.value),
                                label: o.label,
                            }))}
                            disabled={paymentTermsIdProps.disabled || isPaymentTermIdDisabled}
                            label={t('orderSummary.paymentTerms')}
                            placeholder={isPaymentTermIdDisabled ? '-' : t('order.summary.comboboxPlaceholder')}
                            error={error ? t('order.summary.comboboxPlaceholder') : undefined}
                            size="lg"
                            className={cn('col-span-1', { 'mb-1': invalid })}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name={billingMethodProps.name}
                    disabled={billingMethodProps.disabled}
                    defaultValue={billingMethodProps.defaultValue ?? null}
                    render={({ field: { ref, value, onChange }, fieldState: { error, invalid } }) => (
                        <SelectCombobox
                            fieldRef={ref}
                            value={value ?? undefined}
                            onValueChange={billingMethod => {
                                if (billingMethod === 'PARTNER_INVOICE') {
                                    clearField(paymentTermsIdProps.name);
                                    setIsPaymentTermIdDisabled(true);
                                } else {
                                    setIsPaymentTermIdDisabled(false);
                                }
                                billingMethodProps.onBillingMethodChange?.(billingMethod);
                                onChange(billingMethod);
                            }}
                            options={billingMethodProps.options.map(o => ({
                                value: o.value,
                                label: o.label,
                            }))}
                            disabled={billingMethodProps.disabled}
                            label={t('orderSummary.billingMethod')}
                            error={error ? t('order.summary.comboboxPlaceholder') : undefined}
                            size="lg"
                            className={cn('col-span-1', { 'mb-1': invalid })}
                        />
                    )}
                />
                {isPaymentTermIdDisabled && (
                    <span className="font-copy-sm text-subdued col-span-1">
                        {t('orderSummary.disabledPaymentTerm.hint')}
                    </span>
                )}
            </div>
        );
    }
);
